import React from 'react';
import { observer } from 'src/utils/mobx-react';
import Header from 'src/components/Header';
import CustomersBar from 'src/components/CustomersBar';
import Audits from 'src/components/Audits';
import { Reports } from 'src/components/Reports/Reports';
import { useAppStateContext } from 'src/AppState/AppState';
import { TableauDashboard } from 'src/components/TableauDashboards/TableauDashboards';
import { DashboardContent } from 'src/layouts/ReportingPage/ReportingPage.style';
import { Events } from 'src/domains/sportsbook/trading/view/eventDetails/Events';
import Report from 'src/components/Reports/Report.jsx';
import { ComplianceAudits } from 'src/components/ComplianceAudits/ComplianceAudits';
import { EventsTrigger } from 'src/components/EventsTrigger/EventsTrigger';
import { PepXmlRouterWrapper } from 'src/components/FraudAndCompliance/PEPTab/PEP/PepXml/PepXml';
import { ReportsKeyType } from 'src/AppState/ConfigState/types';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { ReportV2 } from 'src/components/Reports/ReportV2';
import { CompliancePlatformReports } from 'src/components/Reports/CompliancePlatformReports';

interface ReportsType {
    type: 'PlatformReports';
    componentType: 'report';
}

interface ReportsArrayType {
    type: ReportsKeyType;
    componentType: 'tableauDashboard';
    suffixUrl: string;
}

interface ReportPagesPropsType {
    tableauDashboards: boolean;
    workbookView: Record<string, string>;
    urlParam: string | null;
    isProdMode: boolean;
}

const ReportPages = observer('ReportingPage', (props: ReportPagesPropsType): React.ReactElement => {
    const { tableauDashboards, workbookView, urlParam } = props;
    const common = useCommon();
    const configState = ConfigState.get(common);
    const isReportsV2 = configState.config.reportsV2;

    let reportsArray: Array<ReportsArrayType | ReportsType> = [
        {
            type: 'PlatformReports',
            componentType: 'report',
        },
    ];

    if (tableauDashboards === true) {
        reportsArray = [
            ...reportsArray,
            {
                type: 'BackofficeReports',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['BackofficeReports'] ?? '',
            },
            {
                type: 'BlockedPlayersReport',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['BlockedPlayersReport'] ?? '',
            },
            {
                type: 'CampaignsDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['CampaignsDashboard'] ?? '',
            },
            {
                type: 'CasinoDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['CasinoDashboard'] ?? '',
            },
            {
                type: 'ComplianceDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['ComplianceDashboard'] ?? '',
            },
            {
                type: 'DepositRestrictions',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['DepositRestrictions'] ?? '',
            },
            {
                type: 'MarketingDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['MarketingDashboard'] ?? '',
            },
            {
                type: 'ProfitAndLossKPIs',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['ProfitAndLossKPIs'] ?? '',
            },
            {
                type: 'RacecourseDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['RacecourseDashboard'] ?? '',
            },
            {
                type: 'RetentionDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['RetentionDashboard'] ?? '',
            },
            {
                type: 'TransactionsDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['TransactionsDashboard'] ?? '',
            },
            {
                type: 'PlatformOverview',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['PlatformOverview'] ?? '',
            },
            {
                type: 'TradingOverview',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['TradingOverview'] ?? '',
            },
            {
                type: 'MarketingOverview',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['MarketingOverview'] ?? '',
            },
            {
                type: 'PaymentsOverview',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['PaymentsOverview'] ?? '',
            },
            {
                type: 'Accounts',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['Accounts'] ?? '',
            },
            {
                type: 'Affiliate',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['Affiliate'] ?? '',
            },
            {
                type: 'AffiliateDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['AffiliateDashboard'] ?? '',
            },
            {
                type: 'Betting',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['Betting'] ?? '',
            },
            {
                type: 'Segmentation',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['Segmentation'] ?? '',
            },
            {
                type: 'PromotionReports',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['PromotionReports'] ?? '',
            },
            {
                type: 'PromoPrediction',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['PromoPrediction'] ?? '',
            },
            {
                type: 'MarketKPIDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['MarketKPIDashboard'] ?? '',
            },
            {
                type: 'AccountBalances',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['AccountBalances'] ?? '',
            },
            {
                type: 'SRGDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['SRGDashboard'] ?? '',
            },
            {
                type: 'Conversions',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['Conversions'] ?? '',
            },
            {
                type: 'RollingNetDeposits',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['RollingNetDeposits'] ?? '',
            },
            {
                type: 'SymplifyDashboard',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['SymplifyDashboard'] ?? '',
            },
            {
                type: 'SRGCompliance',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['SRGCompliance'] ?? '',
            },
            {
                type: 'SymplifyStats',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['SymplifyStats'] ?? '',
            },
            {
                type: 'LiftetimeValue',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['LiftetimeValue'] ?? '',
            },
            {
                type: 'LiveChat',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['LiveChat'] ?? '',
            },
            {
                type: 'BonusEngine',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['BonusEngine'] ?? '',
            },
            {
                type: 'TargetingAttributes',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['TargetingAttributes'] ?? '',
            },
            {
                type: 'CheltenhamOffers',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['CheltenhamOffers'] ?? '',
            },
            {
                type: 'BespokeReports',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['BespokeReports'] ?? '',
            },
            {
                type: 'AuditsCustomerLists',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['AuditsCustomerLists'] ?? '',
            },
            {
                type: 'CustomerRetention',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['CustomerRetention'] ?? '',
            },
            {
                type: 'CasinoAudit',
                componentType: 'tableauDashboard',
                suffixUrl: workbookView['CasinoAudit'] ?? '',
            },
        ];
    }

    const hydrateConfig = reportsArray.find((item) => {
        return item.type === urlParam;
    });

    if (urlParam === 'CompliancePlatformReports') {
        return <CompliancePlatformReports />;
    }

    if (hydrateConfig?.componentType === 'report') {
        return isReportsV2 ? (
            <ReportV2 />
        ) : (
            <Report
                key='reports'
                label='reports'
            />
        );
    } else if (hydrateConfig?.type === urlParam) {
        return (
            <TableauDashboard
                key={hydrateConfig.type}
                workbookView={hydrateConfig.suffixUrl}
            />
        );
    } else {
        return isReportsV2 ? (
            <ReportV2 />
        ) : (
            <Report
                key='reports'
                label='reports'
            />
        );
    }
});

export const ReportingPage = observer('ReportingPage', (): React.ReactElement => {
    const {
        routerBackoffice,
        isProdMode,
        configState: {
            config: { tableauDashboards, workbookView },
        },
    } = useAppStateContext();

    const globalParameters = routerBackoffice.getParametersString('global', 'tab');

    return (
        <div className='content'>
            <Header />
            <Reports tab={globalParameters} />
            <DashboardContent>
                <ReportPages
                    tableauDashboards={tableauDashboards}
                    workbookView={workbookView}
                    urlParam={globalParameters}
                    isProdMode={isProdMode}
                />
            </DashboardContent>
            <CustomersBar />
            <EventsTrigger />
            <Events />
            <Audits />
            <ComplianceAudits />
            <PepXmlRouterWrapper />
        </div>
    );
});
